import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import {
  attributesConditionsMatch,
  canUserCustomAttributeBeViewed,
  getApplicableUserCustomAttributes
} from 'shared-helpers/customAttributes';
import { ContentLabel, ContentValue } from 'components/common';
import { UserCustomAttributeDisplayValue } from 'components/common/customAttributes';
import { Icon } from 'components/common/icons';
import { UsersEdit } from 'components/users';
import { ProfileInfoEdit, ProfileSummarySection } from '.';

import type { JSX } from 'react';
import type { RequestUser } from 'shared-types';

interface Props {
  hideCustomAttributes?: boolean;
  isPreview: boolean;
  isUserPage: boolean;
  targetUser: RequestUser;
}

const ProfileInfo = ({
  hideCustomAttributes = false,
  isPreview,
  isUserPage,
  targetUser
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    serverConfig,
    user: loggedInUser
  } = useReduxState();

  const { userCustomAttributes, userUseName, userUseNameInCertificate } =
    serverConfig;

  const handleUserUpdated = async () => {
    try {
      const usersResponse = await api.get(`/admin/users?id=${targetUser.id}`);
      const updatedUser = usersResponse.data?.users?.[0];
      if (!updatedUser) throw new Error('error');

      dispatch(actions.updatePageState({ user: updatedUser }));
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const profileData: {
    key?: string;
    label: JSX.Element | string;
    value: JSX.Element | string;
  }[] = [
    { label: messages.commonEmail, value: targetUser.email },
    { label: messages.commonUsername, value: targetUser.username },
    ...(userUseName || userUseNameInCertificate
      ? [
          { label: messages.usersFirstName, value: targetUser.firstName },
          { label: messages.usersLastName, value: targetUser.lastName }
        ]
      : [])
  ];

  const applicableUserCustomAttributes = hideCustomAttributes
    ? null
    : getApplicableUserCustomAttributes(
        targetUser,
        userCustomAttributes
      ).filter((attribute) =>
        canUserCustomAttributeBeViewed({
          allowUserPermissionForAdmin: !isUserPage,
          user: loggedInUser,
          userCustomAttribute: attribute
        })
      );

  if (applicableUserCustomAttributes?.length) {
    applicableUserCustomAttributes.forEach((attribute) => {
      const conditionsMatch = attributesConditionsMatch({
        conditions: attribute.conditions,
        customAttributeValues: targetUser.customAttributes,
        user: targetUser
      });

      if (conditionsMatch) {
        const title =
          attribute.titleTranslations?.[language] || attribute.title;

        const label = !attribute.permissions?.view.user.value ? (
          <div style={{ alignItems: 'center', display: 'flex' }}>
            {title}
            <Icon
              data-tooltip={
                attribute.permissions?.view.superadmin.value &&
                !attribute.permissions?.view.admin.value
                  ? messages.userAttributesFormVisibleOnlySuperadmin
                  : messages.userAttributesFormVisibleOnlyAdmin
              }
              icon="eye"
              style={{
                fontSize: '1.2rem',
                marginLeft: 7,
                position: 'relative',
                top: 1
              }}
            />
          </div>
        ) : (
          title
        );

        profileData.push({
          key: attribute.key,
          label,
          value: (
            <UserCustomAttributeDisplayValue
              attribute={attribute}
              value={targetUser.customAttributes[attribute.key]}
            />
          )
        });
      }
    });
  }

  const loggedInUserHasEditPermissions =
    loggedInUser?.roles.includes('superadmin') ||
    (loggedInUser?.roles.includes('admin') &&
      loggedInUser?.permissions.includes('manage-users'));

  return (
    <ProfileSummarySection
      title={
        <div>
          {messages.profileInformation}

          {!isPreview && !isUserPage ? (
            <ProfileInfoEdit
              loggedInUserHasEditPermissions={loggedInUserHasEditPermissions}
              targetUser={targetUser}
            />
          ) : !isPreview &&
            loggedInUserHasEditPermissions &&
            !targetUser?.roles?.includes('guest') &&
            !(
              !loggedInUser?.roles.includes('superadmin') &&
              targetUser?.roles.includes('admin')
            ) ? (
            <UsersEdit
              handleSuccess={handleUserUpdated}
              modalButtonWithFormProps={{
                buttonCategory: 'lighter',
                buttonIcon: 'pen',
                buttonProps: {
                  iconStyle: {
                    fontSize: '0.9rem',
                    marginRight: 3,
                    position: 'relative',
                    top: -2
                  }
                },
                buttonSize: 'small',
                buttonStyle: {
                  borderRadius: 30,
                  marginLeft: 8,
                  padding: '3px 10px 4px 8px',
                  position: 'relative',
                  top: -1
                },
                buttonText: messages.commonEdit
              }}
              targetUser={targetUser}
            />
          ) : null}
        </div>
      }
    >
      <div className="items">
        {profileData.map((item, i) => (
          <ContentLabel
            horizontal={true}
            key={item.key || (item.label as string)}
            label={item.label}
            spacing={15}
          >
            <ContentValue style={{ fontWeight: 600 }}>
              {item.value || '-'}
            </ContentValue>
          </ContentLabel>
        ))}
      </div>

      <style jsx>{`
        .items {
          margin-top: 15px;
        }
      `}</style>
    </ProfileSummarySection>
  );
};

export default ProfileInfo;
